import { useTu } from "@src/appV2/i18n/useTu";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton as MuiIconButton, Stack } from "@mui/material";
import { Button } from "../../components/Button";
import { Text } from "../../components/Text";

interface ChatAdminReadOnlyProps {
  /**
   * When provided, renders an "Invite to shift" CTA below the notice.
   * Caller is responsible for permission / placement / blocked gating.
   */
  onInviteToShift?: () => void;
}

/**
 * Displayed in place of the chat input when an admin views the chat.
 * Visually mirrors `ChatInput` so the chat's primary-action area stays
 * consistent across user types.
 */
export function ChatAdminReadOnly(props: ChatAdminReadOnlyProps) {
  const { tu } = useTu("chat");

  const { onInviteToShift } = props;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 4,
        borderRadius: theme.borderRadius?.xLarge,
        backgroundColor: theme.palette.background?.baseDarker,
      })}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <InfoOutlinedIcon
          sx={(theme) => ({
            fontSize: "20px",
            color: theme.palette.primary.main,
          })}
        />
        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.base?.inactive,
          })}
        >
          {tu("Only workplace users can send messages")}
        </Text>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        {onInviteToShift && (
          <Button
            aria-label={tu("Invite to shift")}
            size="small"
            startIconType="plus"
            variant="outlined"
            onClick={onInviteToShift}
          >
            {tu("Invite to shift")}
          </Button>
        )}

        <Stack direction="row" sx={{ alignItems: "center", gap: 2, marginLeft: "auto" }}>
          <MuiIconButton
            disabled
            aria-label={tu("Attach file")}
            sx={(theme) => ({
              padding: "5px",
              "&.Mui-disabled": {
                color: theme.palette.base?.inactive,
              },
            })}
          >
            <AttachFileIcon sx={{ fontSize: "14px" }} />
          </MuiIconButton>
          <MuiIconButton
            disabled
            aria-label={tu("Send message")}
            sx={(theme) => ({
              backgroundColor: "transparent",
              borderRadius: theme.borderRadius?.xLarge,
              padding: "5px",
              minWidth: "24px",
              minHeight: "24px",
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: theme.palette.base?.inactive,
              },
            })}
          >
            <SendIcon sx={{ fontSize: "14px" }} />
          </MuiIconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
